import { Paginated } from "@/lib/interfaces/utils";
import { DotsVerticalIcon } from "@radix-ui/react-icons";
import { createContext, Dispatch, SetStateAction, useCallback, useContext, useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useUser } from "../../hooks/use-user";
import useApiRequest from "../../lib/hooks/useRequest";
import { parseTemplateCategory } from "../../lib/hooks/utils";
import {
  TemplateCategory,
  TemplateDetailsResponseDto,
  TemplateFeaturedListResponseDto,
  TemplateListPaginatedResponseDtoWithFeaturedAndEntities,
  TemplateListResponseDto,
} from "../../lib/interfaces/template";
import { UserRole, UserType } from "../../lib/interfaces/user";
import AddIcon from "../../ui/components/icons/add-icon";
import TemplatesStarIcon from "../../ui/components/icons/templates-star-icon";
import { Badge } from "../../ui/components/ui/badge";
import { Button } from "../../ui/components/ui/button";
import DataFetchParent from "../../ui/components/ui/data-fetch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../ui/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../../ui/components/ui/dropdown-menu";
import Preloader from "../../ui/components/ui/preloader";
import SearchInput from "../../ui/components/ui/search-input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/components/ui/select";
import TemplateForm from "./template-form";

const TemplateContext = createContext<{
  baseUrl: string;
  setReload: Dispatch<SetStateAction<boolean>>;
  isTemplateAdminMode: boolean;
}>({
  baseUrl: "",
  setReload: () => {},
  isTemplateAdminMode: false,
});

const Templates = () => {
  const { user } = useUser();
  const [templates, setTemplates] = useState<TemplateListPaginatedResponseDtoWithFeaturedAndEntities>();
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const [initLoading, setInitLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const { apiRequest } = useApiRequest();
  const [searchQuery, setSearchQuery] = useState(searchParams.get("textSearch") || "");
  const [selectedCategory, setSelectedCategory] = useState<TemplateCategory | undefined>(
    (searchParams.get("categoryFilter") as TemplateCategory) || undefined
  );
  const baseUrl = user?.userType === UserType.Customer ? "templates/customer" : "templates/internal";
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [reload, setReload] = useState(false);
  const isTemplateAdminMode = user?.userType === UserType.Internal && (user?.userRole === UserRole.SuperAdmin || user?.userRole === UserRole.Admin);

  const getTemplatesInit = useCallback(
    async (searchQuery?: string, selectedCategory?: TemplateCategory) => {
      setInitLoading(true);
      setCurrentPage(1);
      setHasMore(true);
      let url = `${baseUrl}/initial?`;
      if (searchQuery) {
        url += `textSearch=${searchQuery}`;
      }
      if (selectedCategory) {
        url += `${url.includes("?") ? "&" : "?"}categoryFilter=${selectedCategory}`;
      }
      const data = await apiRequest<TemplateListPaginatedResponseDtoWithFeaturedAndEntities>(url, "GET");
      setInitLoading(false);
      if (data.data) {
        setTemplates({
          templates: data.data.templates,
          featuredTemplates: data.data.featuredTemplates,
          filterSelectableEntities: data.data.filterSelectableEntities,
        });
        // Check if there are more pages available
        setHasMore(data.data.templates.page ? data.data.templates.page < (data.data.templates.pageCount || 1) : false);
      }
    },
    [apiRequest, baseUrl]
  );

  const getTemplates = useCallback(
    async (
      searchQuery?: string,
      selectedCategory?: TemplateCategory,
      templates?: TemplateListPaginatedResponseDtoWithFeaturedAndEntities
    ) => {
      setIsLoading(true);
      setCurrentPage(1);
      setHasMore(true);
      let url = `${baseUrl}?page=${1}`;
      if (searchQuery) {
        url += `&textSearch=${searchQuery}`;
      }
      if (selectedCategory) {
        url += `&categoryFilter=${selectedCategory}`;
      }
      const data = await apiRequest<Paginated<TemplateListResponseDto>>(url, "GET");
      setIsLoading(false);
      if (data.data) {
        setTemplates({
          templates: data.data,
          featuredTemplates: templates?.featuredTemplates ?? [],
          filterSelectableEntities: templates?.filterSelectableEntities ?? { categoryFilter: [] },
        });
        // Check if there are more pages available
        setHasMore(data.data.page ? data.data.page < (data.data.pageCount || 1) : false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [apiRequest, baseUrl]
  );

  const loadMoreTemplates = useCallback(async () => {
    if (!hasMore || loadingMore || !templates) return;

    setLoadingMore(true);
    const nextPage = currentPage + 1;
    let url = `${baseUrl}?page=${nextPage}`;
    if (searchQuery) {
      url += `&textSearch=${searchQuery}`;
    }
    if (selectedCategory) {
      url += `&categoryFilter=${selectedCategory}`;
    }

    try {
      const data = await apiRequest<Paginated<TemplateListResponseDto>>(url, "GET");
      if (data.data && data.data.docs) {
        setTemplates((prev) => {
          if (!prev) return prev;
          return {
            templates: {
              ...data.data,
              docs: [...(prev.templates.docs || []), ...(data.data?.docs || [])],
              limit: data.data?.limit || prev.templates.limit || 10,
              offset: data.data?.offset || prev.templates.offset || 0,
              page: data.data?.page || prev.templates.page || 1,
              hasNextPage: data.data?.hasNextPage ?? prev.templates.hasNextPage ?? false,
              hasPrevPage: data.data?.hasPrevPage ?? prev.templates.hasPrevPage ?? false,
              count: data.data?.count || prev.templates.count || 0,
              pageCount: data.data?.pageCount || prev.templates.pageCount || 1,
            },
            featuredTemplates: prev.featuredTemplates,
            filterSelectableEntities: prev.filterSelectableEntities,
          };
        });
        setCurrentPage(nextPage);
        setHasMore(data.data.page ? data.data.page < (data.data.pageCount || 1) : false);
      }
    } catch (error) {
      console.error("Error loading more templates:", error);
    } finally {
      setLoadingMore(false);
    }
  }, [hasMore, loadingMore, templates, currentPage, baseUrl, searchQuery, selectedCategory, apiRequest]);

  useEffect(() => {
    getTemplatesInit(searchQuery, selectedCategory);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getTemplatesInit, reload]);

  useEffect(() => {
    if (!initLoading) {
      getTemplates(searchQuery, selectedCategory, templates);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getTemplates, searchQuery, selectedCategory]);

  // Infinite scroll detection
  useEffect(() => {
    const handleScroll = () => {
      const container = scrollContainerRef.current;
      if (!container || !hasMore || loadingMore) return;

      const { scrollTop, scrollHeight, clientHeight } = container;
      const isNearBottom = scrollTop + clientHeight >= scrollHeight - 200; // Load when 200px from bottom

      if (isNearBottom) {
        loadMoreTemplates();
      }
    };

    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener("scroll", handleScroll);
      return () => container.removeEventListener("scroll", handleScroll);
    }
  }, [hasMore, loadingMore, loadMoreTemplates]);

  return (
    <div>
      <div className="flex flex-col gap-5 h-full">
        <div className="flex justify-between">
          <div>
            <h1 className="text-[32px] leading-[38px] font-bold text-[#12282A]">Vorlagen</h1>
            <p className="text-[16px] text-[#888]">Alle Vorlagen entdecken</p>
          </div>
          <div className="flex gap-2">
            <CategoryFilter selectedCategory={selectedCategory} setSelectedCategory={setSelectedCategory} />
            <SearchInput setSearchText={setSearchQuery} searchText={searchQuery} />
            {(user?.userRole === UserRole.SuperAdmin || user?.userRole === UserRole.Admin) &&
              user?.userType === UserType.Internal &&
              isTemplateAdminMode && <AddTemplate />}
          </div>
        </div>
        <TemplateContext.Provider value={{ baseUrl, setReload, isTemplateAdminMode }}>
          <div ref={scrollContainerRef} className="overflow-auto max-h-[calc(100vh-200px)]">
            <DataFetchParent
              isLoading={initLoading}
              data={templates}
              renderElement={(data: TemplateListPaginatedResponseDtoWithFeaturedAndEntities) => (
                <div className="relative">
                  {isLoading && <Preloader />}
                  <TemplateWrapper templates={data} loadingMore={loadingMore} hasMore={hasMore} />
                </div>
              )}
            />
          </div>
        </TemplateContext.Provider>
      </div>
    </div>
  );
};

const AddTemplate = () => {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <Dialog open={isOpen} onOpenChange={(open) => setIsOpen(open)}>
      <DialogTrigger asChild>
        <Button className="flex gap-4">
          <AddIcon />
          <p>Vorlage erstellen</p>
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md min-w-[600px] py-4 px-6 gap-5 bg-[#F0F4FC]" aria-describedby={undefined}>
        <DialogHeader className="mb-7 items-center flex flex-col">
          <DialogTitle className="font-bold text-2xl">Erstelle eine neue Vorlage</DialogTitle>
          <DialogDescription>
            Erstelle eine neue Vorlage, um sie später in Aufträgen nutzen zu können.
          </DialogDescription>
        </DialogHeader>
        <TemplateForm
          submitCallback={() => {
            setIsOpen(false);
            window.location.reload();
          }}
        />
      </DialogContent>
    </Dialog>
  );
};

const EditTemplate = ({
  id,
  isOpen,
  onOpenChange,
}: {
  id: string;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}) => {
  const { apiRequest } = useApiRequest();
  const [template, setTemplate] = useState<TemplateDetailsResponseDto | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { baseUrl, setReload } = useContext(TemplateContext);

  const getTemplate = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await apiRequest<TemplateDetailsResponseDto>(`${baseUrl}/${id}`, "GET");
      if (data.data) {
        setTemplate(data.data);
      }
    } catch (error) {
      console.error("Error fetching template:", error);
    } finally {
      setIsLoading(false);
    }
  }, [apiRequest, baseUrl, id]);

  useEffect(() => {
    if (isOpen) {
      getTemplate();
    } else {
      // Reset state when dialog closes
      setTemplate(null);
      setIsLoading(false);
    }
  }, [getTemplate, isOpen]);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      onOpenChange(open);
    },
    [onOpenChange]
  );

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md min-w-[600px] py-4 px-6 gap-5 bg-[#F0F4FC]" aria-describedby={undefined}>
        <DialogHeader className="mb-7 items-center flex flex-col">
          <DialogTitle className="font-bold text-2xl">Bearbeite eine Vorlage</DialogTitle>
          <DialogDescription>Bearbeite eine Vorlage, um sie später in Aufträgen nutzen zu können.</DialogDescription>
        </DialogHeader>
        <DataFetchParent
          isLoading={isLoading}
          data={template}
          renderElement={(data: TemplateDetailsResponseDto) => (
            <TemplateForm
              template={data}
              submitCallback={() => {
                handleOpenChange(false);
                setReload((prev) => !prev);
              }}
            />
          )}
        />
      </DialogContent>
    </Dialog>
  );
};

const CategoryFilter = ({
  selectedCategory,
  setSelectedCategory,
}: {
  selectedCategory: TemplateCategory | undefined;
  setSelectedCategory: (category: TemplateCategory | undefined) => void;
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  return (
    <div>
      <Select
        onValueChange={(value) => {
          setSelectedCategory(value === "all" ? undefined : (value as TemplateCategory));
          const newParams = new URLSearchParams(searchParams);
          if (value === "all") {
            newParams.delete("categoryFilter");
          } else {
            newParams.set("categoryFilter", value);
          }
          setSearchParams(newParams);
        }}
        defaultValue={selectedCategory || "all"}
      >
        <SelectTrigger className="gap-3 h-10 items-center rounded-2xl px-3 py-2 border text-sm inline-flex justify-center whitespace-nowrap font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 border-input bg-background hover:bg-accent hover:text-accent-foreground">
          <SelectValue placeholder="Kunde" />
        </SelectTrigger>
        <SelectContent className="">
          <SelectItem value="all">Alle</SelectItem>
          {Object.values(TemplateCategory).map((category) => (
            <SelectItem key={category} value={category}>
              {parseTemplateCategory(category)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
};

const TemplateWrapper = ({
  templates,
  loadingMore,
  hasMore,
}: {
  templates: TemplateListPaginatedResponseDtoWithFeaturedAndEntities;
  loadingMore: boolean;
  hasMore: boolean;
}) => {
  return (
    <div className="flex flex-col gap-8">
      {templates.featuredTemplates.length > 0 && (
        <>
          <div className="grid grid-cols-4 gap-8">
            {templates.featuredTemplates.map((template) => (
              <FeaturedTemplateCard template={template} key={template._id} />
            ))}
          </div>
          <div className="flex items-center gap-10">
            <div className="w-full h-[2px] bg-[#E0E0E0] rounded-full ml-2" />
            <p className="text-sm text-[#888] whitespace-nowrap">Alle Vorlagen</p>
            <div className="w-full h-[2px] bg-[#E0E0E0] rounded-full mr-2" />
          </div>
        </>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {templates.templates.docs.map((template) => (
          <TemplateCard template={template} key={template._id} />
        ))}
      </div>
      {loadingMore && (
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      )}
    </div>
  );
};

const FeaturedTemplateCard = ({ template }: { template: TemplateFeaturedListResponseDto }) => {
  const { isTemplateAdminMode } = useContext(TemplateContext);

  return (
    <div className="bg-white rounded-2xl p-4 flex flex-col gap-4 relative group hover:shadow-lg transition-shadow">
      <Link to={`/order/create?templateId=${template._id}`} className="flex flex-col gap-4">
        <div className="bg-[#F5F5F7] rounded-2xl overflow-hidden">
          <img src={template.imageUrl} alt={template.title} className="w-full h-full object-contain aspect-square" />
        </div>
        <div className="flex justify-between items-center min-w-0">
          <p className="text-[20px] font-bold truncate flex-1 mr-3 min-w-0">{template.title}</p>
          <TemplatesStarIcon className="flex-shrink-0" />
        </div>
      </Link>
      {isTemplateAdminMode && (
        <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
          <TemplateMenu id={template._id} />
        </div>
      )}
    </div>
  );
};

const TemplateCard = ({ template }: { template: TemplateListResponseDto }) => {
  const { isTemplateAdminMode } = useContext(TemplateContext);

  return (
    <div className="bg-white rounded-2xl flex gap-4 p-4 h-48 relative group hover:shadow-lg transition-shadow">
      <Link to={`/order/create?templateId=${template._id}`} className="flex gap-4 flex-1 min-w-0">
        <div className="bg-[#F5F5F7] rounded-2xl overflow-hidden flex-shrink-0">
          <img
            src={template.imageUrl}
            alt={template.title}
            className="flex-shrink-0 w-full h-full object-contain aspect-square max-w-40 max-h-40"
          />
        </div>
        <div className="flex flex-col gap-2 flex-1 justify-center min-w-0">
          <Badge variant="secondary" className="w-fit rounded-full text-[13px] px-3 py-0.5">
            {parseTemplateCategory(template.templateCategory)}
          </Badge>
          <p className="text-[20px] font-bold truncate min-w-0">{template.title}</p>
          <p className="text-[18px] text-[#888] line-clamp-2">{template.description}</p>
        </div>
        <TemplatesStarIcon
          className={`flex-shrink-0 ${
            isTemplateAdminMode ? "group-hover:translate-x-[-30px] transition-transform duration-200" : ""
          }`}
        />
      </Link>
      {isTemplateAdminMode && (
        <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
          <TemplateMenu id={template._id} />
        </div>
      )}
    </div>
  );
};

const TemplateMenu = ({ id }: { id: string }) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const { apiRequest } = useApiRequest();
  const { baseUrl, setReload } = useContext(TemplateContext);

  const handleEdit = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDropdownOpen(false); // Close dropdown first
    // Small delay to allow dropdown to close properly before opening dialog
    setTimeout(() => {
      setIsEditOpen(true);
    }, 100);
  }, []);

  const handleDelete = useCallback(
    async (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDropdownOpen(false);
      await apiRequest(`${baseUrl}/${id}`, "DELETE", {
        toast: { toastText: "Vorlage wurde erfolgreich gelöscht" },
      });
      setReload((prev) => !prev);
    },
    [apiRequest, id, baseUrl, setReload]
  );

  const handleEditOpenChange = useCallback((open: boolean) => {
    setIsEditOpen(open);
  }, []);

  return (
    <>
      <DropdownMenu open={isDropdownOpen} onOpenChange={setIsDropdownOpen}>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className="bg-transparent hover:bg-transparent">
            <DotsVerticalIcon className="w-4 h-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-48">
          <DropdownMenuItem onClick={handleEdit}>Bearbeiten</DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={handleDelete} className="text-red-600 focus:text-red-600">
            Löschen
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <EditTemplate id={id} isOpen={isEditOpen} onOpenChange={handleEditOpenChange} />
    </>
  );
};

export default Templates;
